import { CSSProperties, FC } from 'react';

import { BugoData } from '@shared/api/bugo/bugo.interface';
import { splitDeceasedName } from '@shared/api/fevent/deceasedName.util';
import { ObituaryTheme, obituaryThemeLabels } from '@shared/api/fevent/obituaryTheme';
import { DeathCharacter } from '@shared/miscValues';
import { formatFeventDateString } from '@shared/utils/formatDate';
import frameBuddhistPhotoSide from 'src/assets/obituaryTheme/frame-buddhist-photo-side.png';
import frameBuddhistPlainSide from 'src/assets/obituaryTheme/frame-buddhist-plain-side.png';
import frameCatholicPhotoLeft from 'src/assets/obituaryTheme/frame-catholic-photo-left.png';
import frameCatholicPhotoRight from 'src/assets/obituaryTheme/frame-catholic-photo-right.png';
import frameCatholicPlainLeft from 'src/assets/obituaryTheme/frame-catholic-plain-left.png';
import frameCatholicPlainRight from 'src/assets/obituaryTheme/frame-catholic-plain-right.png';
import frameChristianPhotoSym from 'src/assets/obituaryTheme/frame-christian-photo-sym.png';
import frameChristianPlainSym from 'src/assets/obituaryTheme/frame-christian-plain-sym.png';
import frameDefaultPhotoSide from 'src/assets/obituaryTheme/frame-default-photo-side.png';
import frameDefaultPlainSide from 'src/assets/obituaryTheme/frame-default-plain-side.png';
import frameNationalMeritPhotoLeft from 'src/assets/obituaryTheme/frame-nationalMerit-photo-left.png';
import frameNationalMeritPhotoRight from 'src/assets/obituaryTheme/frame-nationalMerit-photo-right.png';
import frameNationalMeritPlainLeft from 'src/assets/obituaryTheme/frame-nationalMerit-plain-left.png';
import frameNationalMeritPlainRight from 'src/assets/obituaryTheme/frame-nationalMerit-plain-right.png';
import symbolBuddhist from 'src/assets/obituaryTheme/symbol-buddhist.svg';
import symbolCatholic from 'src/assets/obituaryTheme/symbol-catholic.svg';
import symbolChristian from 'src/assets/obituaryTheme/symbol-christian.svg';
import symbolNationalMerit from 'src/assets/obituaryTheme/symbol-nationalMerit.svg';

import { obituaryHeaderBackground } from './obituaryHeaderPalette';
import { ObituaryPortrait } from './ObituaryPortrait';

//* 종교·예우 테마 부고장 헤더 — 디자이너 시안(2026-10-19 preed_religon) 반영
//*
//* 어셋 규칙
//* - 프레임 PNG는 @2x 내보내기(사진형 378px·사진없음형 296px). slice 값은 이미지 px, 화면값은 ×FRAME_SCALE.
//*   설계 배율 0.5(=189/148px)는 복잡한 무늬에 비해 너무 작게 읽혀 0.6으로(2026-11-02 사용자). 0.7은 천주교 장식이
//*   영정과 부딪히고 국가유공자 괄호가 무거워짐.
//* - 테두리는 CSS border-image로 그린다: 위·아래 장식(slice)은 원본 비율 유지, 가운데 선만 늘어나므로
//*   이름이 3줄로 길어져 헤더가 커져도 장식이 찌그러지지 않는다(단순 img h-full은 장식이 늘어남).
//* - 디자이너 명명(2026-11-02 재정리 preed_religion2): Ttype1=사진 있음(photo)·Ttype2=사진 없음(plain),
//*   Frame-1=왼쪽·Frame-2=오른쪽(천주교·국가유공자), 접미사 없는 Frame=좌우 동일(불교·기독교 → 오른쪽은 좌우반전).
//*   (시안 주석의 '오른쪽/왼쪽' 글자는 서로 바뀌어 있지만 픽셀 배치는 Frame-1 왼쪽·Frame-2 오른쪽이 맞음 — 물고기가 오른쪽 아래)
//* - 기독교는 네 변이 한 장(268px 폭)이지만 꽃장식이 왼쪽 두 귀에만 있다 → 오른쪽 세로선 열을 떼고 좌우대칭으로 합성한
//*   *-sym.png(PIL, 원본 *-full.png는 디자이너 소스로 보관, 반투명 선 알파 ×1.6 보강)를 네 귀 slice 한 장으로 두른다. 두 반쪽을 50%씩 붙이면
//*   홀수 폭 기기에서 반픽셀 이음새가 보였음(선이 반투명이라 겹쳐도 티남) — 합성 한 장은 경계 픽셀이 같아 이음새 없음.
//*
//* 배경: 팔레트(obituaryHeaderPalette) 기본색 위에 아주 옅은 세로 그라데이션 + 상징 뒤 미세한 광원(2026-11-02 사용자 제안). 선 프레임이 반투명 흰색이라
//*   대비를 흔들지 않게 ±4% 명도 안에서만. 빼려면 obituaryHeaderBackground의 backgroundImage만 지우면 됨.
//* - 상징(svg)은 흰색 고정이라 img 로 둔다. 사진형은 이름 위 가운데, 사진없음형은 왼쪽 세로 중앙(시안 그대로).
//*
//* 레이아웃은 시안 기준: 사진형 = 영정 + 오른쪽 가운데 정렬 텍스트, 사진없음형 = 전체 가운데.
//* 영정은 시안 118×150(헤더 189의 78%)이 실기기에서 꽉 차 보여 100×128 + 얇은 흰 선·안쪽 여백(매트 액자)으로, 헤더는 200px로.
//* 텍스트: 사진형은 시안대로 3줄 "故 이름 (상세)" / "남 / 92세" / "2026년 10월 15일 소천", 訃告 제목 없음(영정 옆 좁은 칸).
//*   사진없음형은 높이 여유가 있어 기본 헤더와 같은 3줄 구조 — 訃告 / "故 이름 (상세) (남/92세)" / 별세일(2026-11-02 사용자 결정).

//* 이미지 px → 화면 px 배율
const FRAME_SCALE = 0.6;

interface SidePiece {
  src: string;
  width: number;
  top: number;
  bottom: number;
  //* 한 장짜리 프레임을 오른쪽에도 쓸 때 좌우반전
  mirror?: boolean;
}
//* 네 변 한 장(좌우대칭 합성본)을 헤더 전체에 두르는 프레임. 네 귀 slice는 좌우 같은 값
interface FullPiece {
  src: string;
  top: number;
  bottom: number;
  side: number;
}
type FrameSpec = { sides: [SidePiece, SidePiece] } | { full: FullPiece };
interface ThemeAssets {
  //* default(무교) 테마는 상징이 없다
  symbol?: string;
  photo: FrameSpec;
  plain: FrameSpec;
}

//* slice 값은 scripts 없이 PIL로 잰 장식 끝 좌표(가운데 행·열과 달라지는 마지막 px)
const buddhistPhoto: SidePiece = {
  src: frameBuddhistPhotoSide,
  width: 88,
  top: 88,
  bottom: 86,
};
const buddhistPlain: SidePiece = {
  src: frameBuddhistPlainSide,
  width: 88,
  top: 88,
  bottom: 86,
};

const defaultPhoto: SidePiece = {
  src: frameDefaultPhotoSide,
  width: 100,
  top: 95,
  bottom: 95,
};
const defaultPlain: SidePiece = {
  src: frameDefaultPlainSide,
  width: 100,
  top: 95,
  bottom: 95,
};

export const obituaryThemeAssets: Record<ObituaryTheme, ThemeAssets> = {
  buddhist: {
    symbol: symbolBuddhist,
    photo: { sides: [buddhistPhoto, { ...buddhistPhoto, mirror: true }] },
    plain: { sides: [buddhistPlain, { ...buddhistPlain, mirror: true }] },
  },
  catholic: {
    symbol: symbolCatholic,
    photo: {
      sides: [
        { src: frameCatholicPhotoLeft, width: 114, top: 99, bottom: 97 },
        //* 오른쪽 아래 물고기(익투스) 장식이 커서 bottom slice가 다름
        { src: frameCatholicPhotoRight, width: 114, top: 98, bottom: 72 },
      ],
    },
    //* 사진없음형 오른쪽도 물고기 장식 — 처음엔 왼쪽 반전으로 썼으나 2차 어셋(Ttype2-Catholic-Frame-2)로 교체
    plain: {
      sides: [
        { src: frameCatholicPlainLeft, width: 114, top: 99, bottom: 97 },
        { src: frameCatholicPlainRight, width: 108, top: 98, bottom: 72 },
      ],
    },
  },
  christian: {
    symbol: symbolChristian,
    //* side slice = 꽃장식 폭(원본 left 끝 열), 합성본이라 오른쪽도 같은 값
    photo: {
      full: { src: frameChristianPhotoSym, top: 121, bottom: 121, side: 49 },
    },
    plain: {
      full: { src: frameChristianPlainSym, top: 125, bottom: 123, side: 53 },
    },
  },
  //* 3차 어셋(preed_religon3, 2026-11-02) 재디자인: 96px 폭 괄호형, 좌·우 각자
  nationalMerit: {
    symbol: symbolNationalMerit,
    photo: {
      sides: [
        { src: frameNationalMeritPhotoLeft, width: 96, top: 96, bottom: 96 },
        { src: frameNationalMeritPhotoRight, width: 96, top: 96, bottom: 96 },
      ],
    },
    plain: {
      sides: [
        { src: frameNationalMeritPlainLeft, width: 96, top: 96, bottom: 96 },
        { src: frameNationalMeritPlainRight, width: 96, top: 96, bottom: 96 },
      ],
    },
  },
  //* 무교(일반): 모서리 선 장식만, 상징 없음(3차 어셋 default-Frame). 한 장이라 오른쪽은 반전
  default: {
    photo: { sides: [defaultPhoto, { ...defaultPhoto, mirror: true }] },
    plain: { sides: [defaultPlain, { ...defaultPlain, mirror: true }] },
  },
};

const px = (value: number) => `${Math.round(value * FRAME_SCALE * 10) / 10}px`;

const sidePieceStyle = (piece: SidePiece, side: 'left' | 'right'): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [side]: 0,
  width: px(piece.width),
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderWidth: `${px(piece.top)} 0 ${px(piece.bottom)} 0`,
  //* fill: 가운데(세로 선) 영역도 그린다 — 없으면 위·아래 장식만 남음
  borderImage: `url(${piece.src}) ${piece.top} 0 ${piece.bottom} 0 fill / ${px(
    piece.top,
  )} 0 ${px(piece.bottom)} 0 / 0 stretch`,
  transform: piece.mirror ? 'scaleX(-1)' : undefined,
  pointerEvents: 'none',
});

const fullPieceStyle = (piece: FullPiece): CSSProperties => ({
  position: 'absolute',
  //* inset 축약형은 iOS 14.5 미만에서 무시돼 프레임이 안 그려진다
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderWidth: `${px(piece.top)} ${px(piece.side)} ${px(piece.bottom)} ${px(piece.side)}`,
  borderImage: `url(${piece.src}) ${piece.top} ${piece.side} ${piece.bottom} ${
    piece.side
  } / ${px(piece.top)} ${px(piece.side)} ${px(piece.bottom)} ${px(
    piece.side,
  )} / 0 stretch`,
  pointerEvents: 'none',
});

const ThemeFrame: FC<{ spec: FrameSpec }> = function ThemeFrame({ spec }) {
  if ('full' in spec) {
    return (
      <span aria-hidden="true" data-frame="full" style={fullPieceStyle(spec.full)} />
    );
  }
  const [left, right] = spec.sides;
  return (
    <>
      <span aria-hidden="true" data-frame="left" style={sidePieceStyle(left, 'left')} />
      <span
        aria-hidden="true"
        data-frame="right"
        style={sidePieceStyle(right, 'right')}
      />
    </>
  );
};

export type ObituaryHeaderInfo = Pick<
  BugoData['fevent']['deceasedInfo'],
  'name' | 'nameDetail' | 'sex' | 'age' | 'death' | 'bugoTerm' | 'religion' | 'dpImage'
>;

//* 추가 이름은 종교와 무관하게 라벨 없이 이름 옆에 표시한다(세례명·법명·직책 중 무엇인지 입력값만으론 알 수 없음)
//* 故는 기본 헤더와 같은 이유로 바탕체(본문 글꼴에 한자 없음)
//* 글자 비율(2026-11-02 사용자): 이름 21px 굵게 > 추가 이름 17px > 성별/나이 16px > 별세일 15px.
//*   예전 19/19/14/14는 세례명이 이름과 같은 크기로 읽히고 성별/나이·별세일이 확 작아져 잘 안 보였음.
//*   기본 헤더(ObituaryHeader)의 21/17 비율과 맞춘다 — 訃告 제목이 없는 만큼 아래 두 줄은 기본 헤더(14)보다 한 단계 크게.
//* sexAgeInline: 성별/나이를 별도 줄 대신 이름 줄 뒤 괄호로(기본 헤더 DeceasedNameHeading과 같은 표기) — 사진없음형 전용
const ThemedDeceasedLines: FC<{ deceasedInfo: ObituaryHeaderInfo; sexAgeInline?: boolean }> =
  function ThemedDeceasedLines({ deceasedInfo, sexAgeInline }) {
    const { sex, age, death, bugoTerm } = deceasedInfo;
    const { baseName, extraName } = splitDeceasedName(deceasedInfo);
    const hasSexAge = Boolean(sex || age);
    return (
      <>
        <p className="break-keep text-[21px] font-bold leading-7" data-line="name">
          <span className="batang text-[1.07em]">{DeathCharacter}</span> {baseName}
          {extraName && (
            <>
              {' '}
              <span className="align-middle text-17 font-medium text-white/80">
                {extraName}
              </span>
            </>
          )}
          {sexAgeInline && hasSexAge && (
            <>
              {' '}
              <span className="whitespace-nowrap align-middle text-17 font-normal text-white/80">
                ({sex ? sex : ''}
                {sex && age ? '/' : ''}
                {age ? `${age}세` : ''})
              </span>
            </>
          )}
        </p>
        {!sexAgeInline && hasSexAge && (
          <p className="mt-1 text-16 leading-6" data-line="sexAge">
            {sex}
            {sex && age ? ' / ' : ''}
            {age ? `${age}세` : ''}
          </p>
        )}
        {death?.date && (
          <p className="mt-0.5 text-15 leading-6" data-line="death">
            {formatFeventDateString(death, false)}&nbsp;{bugoTerm || '별세'}
          </p>
        )}
      </>
    );
  };

interface Props {
  deceasedInfo: ObituaryHeaderInfo;
  theme: ObituaryTheme;
  portraitSrc?: string;
}

export const ObituaryThemedHeader: FC<Props> = function ObituaryThemedHeader({
  deceasedInfo,
  theme,
  portraitSrc,
}) {
  const assets = obituaryThemeAssets[theme];
  const label = obituaryThemeLabels[theme];
  if (portraitSrc || deceasedInfo.dpImage) {
    return (
      <header
        className="relative flex min-h-[200px] items-center gap-4 py-6 px-7 text-white"
        style={obituaryHeaderBackground}
        data-theme={theme}
        data-layout="photo"
        aria-label={`${label} 부고`}
      >
        <ThemeFrame spec={assets.photo} />
        <ObituaryPortrait portraitSrc={portraitSrc} dpImage={deceasedInfo.dpImage} />
        <div className="relative flex min-w-0 flex-1 flex-col items-center text-center">
          {assets.symbol && (
            <img src={assets.symbol} className="h-9 w-auto" alt="" aria-hidden="true" />
          )}
          <div className={assets.symbol ? 'mt-3 w-full' : 'w-full'}>
            <ThemedDeceasedLines deceasedInfo={deceasedInfo} />
          </div>
        </div>
      </header>
    );
  }
  //* 상징은 왼쪽 세로 중앙(시안), 텍스트는 상징 열을 뺀 남은 폭에서 가운데 — 사진형(영정 + 남은 폭 가운데)과 같은 규칙.
  //* 처음엔 상징을 절대배치하고 텍스트를 헤더 전체 기준 가운데(좌우 72px)로 뒀는데, "故 홍길동 어거스틴 (남/87세)"처럼
  //* 긴 이름이 왼쪽 72px까지 차면 상징에 바짝 붙고 오른쪽만 비어 보였음(2026-11-02 사용자). flex 열로 두면 간격이 항상 16px.
  //* 여백: 왼쪽 36px(상징 자리, 시안)·오른쪽 28px(프레임 안전 한계) + 16px 스페이서. 눈은 "상징 오른쪽 끝 ~ 헤더 오른쪽 끝"을
  //* 남은 공간으로 읽어서 오른쪽 56px은 글이 왼쪽으로 치우쳐 보였고, 28px은 완전 가운데일 필요가 없어 살짝 왼쪽으로(2026-11-02 사용자).
  //* 스페이서로 둔 이유: 여백을 패딩으로 고정하면 세례명이 길 때 빈 공간이 남아도 줄이 꺾임(2026-11-02 사용자). 글이 넉넉하면 16px을
  //* 그대로 차지해 글이 8px 왼쪽, 글이 길면 텍스트 열보다 먼저 0까지 줄어 그 공간을 글이 쓴다(텍스트 열은 flex-auto라 글 길이가 기준폭).
  //* 간격은 상징 mr-4 — flex gap은 폭 0인 스페이서 앞에도 남아 공간을 못 쓰게 된다.
  //* 안전 근거(PIL 실측): 사진없음형 프레임은 네 귀 장식(폭 53~68px·높이 52px)만 있고 가운데 세로선이 없음(기독교만 가장자리 7~10px).
  //* 장식이 세로 중앙 띠에 가까운 20px 구간에서 안쪽으로 뻗는 폭은 최대 17px(천주교)이라 이름이 2줄로 꺾여도 안 닿음.
  //* 상징 없는 무교 테마는 좌우 같은 여백으로 완전 가운데.
  return (
    <header
      className={`relative flex min-h-[160px] items-center py-5 text-white ${
        assets.symbol ? 'pl-9 pr-7' : 'px-14'
      }`}
      style={obituaryHeaderBackground}
      data-theme={theme}
      data-layout="plain"
      aria-label={`${label} 부고`}
    >
      <ThemeFrame spec={assets.plain} />
      {assets.symbol && (
        <img
          src={assets.symbol}
          className="relative mr-4 h-11 w-auto shrink-0"
          alt=""
          aria-hidden="true"
          data-symbol="side"
        />
      )}
      <div className="relative min-w-0 flex-auto text-center" data-text-column="plain">
        <p className="batang mb-2 text-30 font-bold leading-9" data-line="title">
          訃告
        </p>
        <ThemedDeceasedLines deceasedInfo={deceasedInfo} sexAgeInline />
      </div>
      {assets.symbol && (
        <span
          className="flex-[0_9999_16px]"
          aria-hidden="true"
          data-spacer="plain"
        />
      )}
    </header>
  );
};
